/**
 * Job Hunter — one match in full: why it scored, the posting, the prepared
 * application (cover note, answers, open questions) and its history. Every
 * decision made here is the same one WhatsApp makes.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  ArrowUpRight, Award, Bookmark, BookmarkCheck, Briefcase, Building2, Check, CircleCheck, Clock, Copy, ExternalLink, FileText, Languages,
  LoaderCircle, MapPin, MessageCircle, RefreshCw, Send, SkipForward, Sparkles, TriangleAlert, Undo2, Wallet, X,
} from 'lucide-react';
import { Button } from '@/components/Button';
import { jobHunterService, type JhApplication, type JhDraft, type JhStatus } from '@/services/job-hunter.service';
import { FEEDBACK_LABELS, REMOTE_LABEL, STATUS_META, copyText, fmtDate, fmtDateTime, timeAgo } from '@/lib/job-hunter/format';
import { useJobHunter } from '@/lib/job-hunter/store';
import { AgentChip, AtsBadge, CountryTag, Drawer, EmployerAvatar, Notice, Pill, ScoreRing, SignalBadge, StatusBadge, VisaLine, highlight } from './ui';

type Tab = 'match' | 'description' | 'application' | 'activity';
const SPONSOR_WORDS = ['visa', 'sponsor', 'sponsorship', 'relocation', 'relocate', 'blue card', 'work permit', 'kennismigrant'];

export function ApplicationDrawer({ applicationUid, onClose, onChanged }: { applicationUid: string | null; onClose: () => void; onChanged?: (a: JhApplication) => void }) {
  const [app, setApp] = useState<JhApplication | null>(null);
  const [tab, setTab] = useState<Tab>('match');
  const [busy, setBusy] = useState<string | null>(null);
  const [cover, setCover] = useState('');
  const [answers, setAnswers] = useState<JhDraft['answers']>([]);
  const [pending, setPending] = useState<Record<string, string>>({});
  const [remember, setRemember] = useState(true);
  const [notes, setNotes] = useState('');
  const [externalRef, setExternalRef] = useState('');
  const [skipOpen, setSkipOpen] = useState(false);
  const { agents } = useJobHunter();

  const apply = useCallback((a: JhApplication) => {
    setApp(a);
    setCover(a.draft?.coverNote ?? '');
    setAnswers(a.draft?.answers ?? []);
    setNotes(a.notes ?? '');
    setExternalRef(a.externalRef ?? '');
    onChanged?.(a);
  }, [onChanged]);

  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!applicationUid) { setApp(null); return; }
    let live = true;
    setApp(null); setTab('match'); setPending({}); setSkipOpen(false);
    jobHunterService.application(applicationUid).then((a) => { if (live) { setApp(a); setCover(a.draft?.coverNote ?? ''); setAnswers(a.draft?.answers ?? []); setNotes(a.notes ?? ''); setExternalRef(a.externalRef ?? ''); } }).catch(() => { if (live) closeRef.current(); });
    return () => { live = false; };
  }, [applicationUid]);

  const run = async (key: string, fn: () => Promise<JhApplication>, ok?: string) => {
    setBusy(key);
    try { const a = await fn(); apply(a); if (ok) toast.success(ok); return a; }
    catch { return null; }
    finally { setBusy(null); }
  };

  const decide = (action: 'apply' | 'skip' | 'save' | 'unsave', reason?: string) => run(action, () => jobHunterService.decide(app!.applicationUid, action, reason),
    action === 'apply' ? 'Approved — application prepared' : action === 'skip' ? 'Skipped' : action === 'save' ? 'Saved for later' : 'Moved back to matches')
    .then((a) => { if (a && action === 'apply') setTab('application'); setSkipOpen(false); });
  const setStatus = (status: JhStatus, label: string) => run(status, () => jobHunterService.updateApplication(app!.applicationUid, { status }), label);

  const job = app?.job;
  const e = app?.evaluation;
  const d = app?.draft;
  const link = job?.applyUrl ?? job?.url;
  const openStatus = app && ['proposed', 'saved'].includes(app.status);
  const prepStatus = app && ['approved', 'needs_input', 'ready'].includes(app.status);
  const agent = app?.agentUid ? agents?.find((a) => a.agentUid === app.agentUid) : undefined;
  const cvLabel = app?.cvLabel ?? (agent?.cv && (!app?.cvUid || agent.cv.cvUid === app.cvUid) ? agent.cv.label : undefined);

  return (
    <Drawer open={!!applicationUid} onClose={onClose}>
      {!app || !e ? (
        <div className="grid flex-1 place-items-center"><LoaderCircle className="animate-spin text-primary" size={26} /></div>
      ) : (
        <>
          <div className="jh-drawer-head">
            <div className="flex items-start gap-4">
              <EmployerAvatar name={job?.employerName} size={48} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold text-text-muted">
                  <span className="jh-chip">#{app.ref}</span>
                  <StatusBadge status={app.status} />
                  <SignalBadge signal={e.sponsorshipSignal} />
                  {job && <AtsBadge fromAts={job.fromAts} type={job.sourceType} />}
                  {app.jobClosed && <Pill tone="danger">Posting closed</Pill>}
                  {e.isAgency && <Pill tone="warning">Agency</Pill>}
                  {agent && <AgentChip name={agent.name} color={agent.color} />}
                </div>
                <h2 className="mt-2 text-[19px] font-bold leading-snug tracking-tight">{job?.title ?? 'Job'}</h2>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] text-text-secondary">
                  <span className="inline-flex items-center gap-1.5 font-semibold text-text-primary"><Building2 size={13} />{job?.employerName}</span>
                  <CountryTag cc={e.country ?? job?.country} city={job?.city} />
                  <span className="inline-flex items-center gap-1"><MapPin size={12} />{REMOTE_LABEL[e.remoteType ?? job?.remoteType ?? 'unknown']}</span>
                  <span className="inline-flex items-center gap-1"><Clock size={12} />Posted {timeAgo(job?.postedAt ?? job?.firstSeenAt)}</span>
                </div>
              </div>
              <ScoreRing score={e.fitScore} size={64} stroke={6} />
              <button type="button" onClick={onClose} aria-label="Close" className="-mr-2 -mt-1 grid h-8 w-8 place-items-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-text-primary"><X size={17} /></button>
            </div>
            <div className="jh-seg mt-4">
              {([['match', 'Why it matched'], ['description', 'Posting'], ['application', `Application${d?.pendingQuestions.length ? ` · ${d.pendingQuestions.length}?` : ''}`], ['activity', 'Activity']] as Array<[Tab, string]>).map(([k, l]) => (
                <button key={k} type="button" aria-pressed={tab === k} onClick={() => setTab(k)}>{l}</button>
              ))}
            </div>
          </div>

          <div className="jh-drawer-body">
            {app.duplicateWarning && <Notice tone="warning" title="Applied here recently">{app.duplicateWarning}</Notice>}
            {app.jobClosed && <Notice tone="danger" title="This posting has closed">The employer removed it from their careers feed. It can't be approved any more.</Notice>}

            {tab === 'match' && (
              <div className="space-y-4">
                {e.summary && <div className="jh-card jh-card-pad text-[13.5px] leading-relaxed"><Sparkles size={14} className="mb-1 mr-1.5 inline text-primary" />{e.summary}</div>}
                <div className="jh-card jh-card-pad">
                  <div className="jh-card-title"><SignalBadge signal={e.sponsorshipSignal} /> Sponsorship</div>
                  {e.sponsorshipQuote ? <p className="jh-quote mt-3">“{e.sponsorshipQuote}”</p> : <p className="jh-muted mt-2">The posting has no sentence about visas or relocation.</p>}
                  {(e.visaRoute || e.registerMatch) && <div className="mt-3 border-t border-border pt-3"><VisaLine route={e.visaRoute} register={e.registerMatch} /></div>}
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="jh-card jh-card-pad">
                    <div className="jh-card-title"><CircleCheck size={15} className="text-success" />Fit reasons</div>
                    <ul className="mt-3 space-y-2 text-[13px]">{e.fitReasons.length ? e.fitReasons.map((r) => <li key={r} className="flex gap-2"><Check size={14} className="mt-0.5 shrink-0 text-success" />{r}</li>) : <li className="jh-muted">—</li>}</ul>
                  </div>
                  <div className="jh-card jh-card-pad">
                    <div className="jh-card-title"><TriangleAlert size={15} className="text-warning" />Red flags</div>
                    <ul className="mt-3 space-y-2 text-[13px]">{e.redFlags.length ? e.redFlags.map((r) => <li key={r} className="flex gap-2"><TriangleAlert size={13} className="mt-0.5 shrink-0 text-warning" />{r}</li>) : <li className="jh-muted">None found.</li>}</ul>
                  </div>
                </div>
                <div className="jh-card overflow-hidden">
                  <table className="jh-table">
                    <tbody>
                      {[
                        [<Award size={13} key="i" />, 'Seniority', e.seniority],
                        [<Languages size={13} key="i" />, 'Language', e.languageRequirement],
                        [<Wallet size={13} key="i" />, 'Salary', e.salaryHint ?? job?.salaryText],
                        [<Briefcase size={13} key="i" />, 'Employment', [job?.employmentType, job?.department].filter(Boolean).join(' · ')],
                        [<MapPin size={13} key="i" />, 'Location', job?.locationRaw],
                        [<FileText size={13} key="i" />, 'Source', job ? `${job.sourceType}${job.seenOn && job.seenOn.length > 1 ? ` · seen on ${job.seenOn.length} boards` : ''}` : ''],
                        [<Clock size={13} key="i" />, 'First seen', fmtDateTime(job?.firstSeenAt)],
                        [<Sparkles size={13} key="i" />, 'Found by', agent ? `${agent.name}${cvLabel ? ` · scored with “${cvLabel}”` : ''}` : ''],
                      ].map(([icon, k, v]) => (
                        <tr key={String(k)}><td className="w-40 text-text-muted"><span className="inline-flex items-center gap-2">{icon}{k}</span></td><td className="font-medium">{(v as string) || '—'}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {link && <a href={link} target="_blank" rel="noreferrer"><Button size="sm" variant="secondary" iconRight={<ExternalLink size={13} />}>Open posting</Button></a>}
                  {job && <Button size="sm" variant="ghost" loading={busy === 'verify'} iconLeft={<RefreshCw size={13} />} onClick={async () => {
                    setBusy('verify');
                    try { const r = await jobHunterService.verifyJob(job.jobUid); toast[r.isOpen ? 'success' : 'error'](r.checked ? (r.isOpen ? 'Posting is still open' : 'Posting has closed') : 'Could not reach the posting'); const a = await jobHunterService.application(app.applicationUid); apply(a); }
                    catch { /* toasted */ } finally { setBusy(null); }
                  }}>Re-check it's open</Button>}
                </div>
                {openStatus && (
                  <div className="jh-card jh-card-pad">
                    <div className="jh-card-title">Not a good match?</div>
                    <p className="jh-card-sub">Your reason teaches the agent. Two “no sponsorship” answers pause the employer.</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {Object.entries(FEEDBACK_LABELS).map(([k, l]) => (
                        <button key={k} type="button" className="jh-chip hover:bg-danger-soft hover:text-danger" disabled={!!busy} onClick={() => run('feedback', () => jobHunterService.feedback(app.applicationUid, { reason: k }), 'Thanks — skipped and noted')}>{l}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {tab === 'description' && (
              <div className="jh-card jh-card-pad">
                {job?.descriptionText ? <div className="jh-desc">{highlight(job.descriptionText, [...SPONSOR_WORDS, ...(job.sponsorshipHits ?? [])])}</div> : <p className="jh-muted">No description stored for this posting.</p>}
              </div>
            )}

            {tab === 'application' && (
              <div className="space-y-4">
                {!d ? (
                  <div className="jh-hero text-center">
                    <Sparkles className="mx-auto text-primary" size={26} />
                    <div className="mt-3 text-[15px] font-semibold">No application prepared yet</div>
                    <p className="jh-muted mx-auto mt-1 max-w-md">Approving prepares a cover note and screening answers from the candidate's truth file. You can also draft it first and decide later.</p>
                    <div className="mt-4 flex justify-center gap-2">
                      {openStatus && <Button loading={busy === 'apply'} iconLeft={<Send size={14} />} onClick={() => decide('apply')} disabled={app.jobClosed}>Approve &amp; prepare</Button>}
                      <Button variant="secondary" loading={busy === 'draft'} iconLeft={<Sparkles size={14} />} onClick={() => run('draft', () => jobHunterService.redraft(app.applicationUid), 'Draft ready')}>Draft only</Button>
                    </div>
                  </div>
                ) : (
                  <>
                    {link && (
                      <div className="jh-card jh-card-pad flex flex-wrap items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="jh-eyebrow">Apply on the employer's site</div>
                          <a href={link} target="_blank" rel="noreferrer" className="mt-1 block truncate text-[13px] font-semibold text-primary hover:underline">{link}</a>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="secondary" iconLeft={<Copy size={13} />} onClick={async () => toast[(await copyText(cover)) ? 'success' : 'error']('Cover note copied')}>Copy cover note</Button>
                          <a href={link} target="_blank" rel="noreferrer"><Button size="sm" iconRight={<ArrowUpRight size={13} />}>Open form</Button></a>
                        </div>
                      </div>
                    )}

                    {d.pendingQuestions.length > 0 && (
                      <div className="jh-card jh-card-pad border-warning/40">
                        <div className="jh-card-title text-warning"><MessageCircle size={15} />{d.pendingQuestions.length} question{d.pendingQuestions.length === 1 ? '' : 's'} need your answer</div>
                        <p className="jh-card-sub">The truth file doesn't cover these, so the agent won't guess. Answers can be kept for future applications.</p>
                        <div className="mt-3 space-y-3">
                          {d.pendingQuestions.map((q) => (
                            <div key={q} className="rounded-xl border border-border bg-surface-alt/50 p-3">
                              <div className="text-[13px] font-semibold">{q}</div>
                              <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                                <input className="jh-input" placeholder="Your answer" value={pending[q] ?? ''} onChange={(ev) => setPending((m) => ({ ...m, [q]: ev.target.value }))} />
                                <Button size="sm" className="h-10" disabled={!pending[q]?.trim()} loading={busy === `answer:${q}`} onClick={() => run(`answer:${q}`, () => jobHunterService.answer(app.applicationUid, { question: q, answer: pending[q], remember }), 'Answer saved')}>Save</Button>
                              </div>
                            </div>
                          ))}
                          <label className="flex items-center gap-2 text-xs text-text-secondary"><input type="checkbox" className="accent-primary" checked={remember} onChange={(ev) => setRemember(ev.target.checked)} />Remember answers in the truth file for future applications</label>
                        </div>
                      </div>
                    )}

                    <div className="jh-card jh-card-pad">
                      <div className="flex items-center justify-between gap-2">
                        <div className="jh-card-title"><FileText size={15} className="text-primary" />Cover note</div>
                        <div className="flex gap-1.5">
                          <Button size="sm" variant="ghost" iconLeft={<Copy size={13} />} onClick={async () => toast[(await copyText(cover)) ? 'success' : 'error']('Copied')}>Copy</Button>
                          <Button size="sm" variant="ghost" loading={busy === 'draft'} iconLeft={<RefreshCw size={13} />} onClick={() => run('draft', () => jobHunterService.redraft(app.applicationUid), 'Draft regenerated')}>Regenerate</Button>
                        </div>
                      </div>
                      {!!d.removedClaims?.length && (
                        <div className="mt-3 rounded-xl border border-border bg-surface-alt/50 px-3.5 py-2.5 text-[12.5px] text-text-secondary">
                          <div className="font-semibold text-text-primary">Fact-check removed {d.removedClaims.length} claim{d.removedClaims.length === 1 ? '' : 's'} your CV doesn’t support</div>
                          <ul className="mt-1 list-disc space-y-0.5 pl-4">{d.removedClaims.map((c) => <li key={c}><s>{c}</s></li>)}</ul>
                          <span className="text-text-muted">If one of these is true, add it to the CV or truth file and regenerate.</span>
                        </div>
                      )}
                      {!!d.unverifiedClaims?.length && (
                        <div className="mt-3">
                          <Notice tone="warning" title="Check before you send — not found in your CV">
                            <ul className="list-disc space-y-0.5 pl-4">{d.unverifiedClaims.map((c) => <li key={c}>{c}</li>)}</ul>
                            <span className="text-text-muted">The AI said it could quote your CV for these and couldn't. Edit them out, or add the fact to the CV / truth file if it is true.</span>
                          </Notice>
                        </div>
                      )}
                      <textarea className="jh-input mt-3" rows={8} value={cover} onChange={(ev) => setCover(ev.target.value)} />
                      {cover !== d.coverNote && <div className="mt-2 flex justify-end"><Button size="sm" loading={busy === 'cover'} onClick={() => run('cover', () => jobHunterService.updateApplication(app.applicationUid, { coverNote: cover }), 'Cover note saved')}>Save cover note</Button></div>}
                    </div>

                    {d.cvHighlights.length > 0 && (
                      <div className="jh-card jh-card-pad">
                        <div className="jh-card-title"><Award size={15} className="text-primary" />Put these first on the CV</div>
                        <p className="jh-card-sub">Reorder only — nothing here is new.</p>
                        <ul className="mt-3 space-y-1.5 text-[13px]">{d.cvHighlights.map((h) => <li key={h} className="flex gap-2"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />{h}</li>)}</ul>
                      </div>
                    )}

                    <div className="jh-card jh-card-pad">
                      <div className="jh-card-title"><MessageCircle size={15} className="text-primary" />Screening answers</div>
                      <p className="jh-card-sub">From the truth file{answers.some((a) => a.source === 'user') ? ' and your answers' : ''}. Edit before you paste them into the form.</p>
                      <div className="mt-3 space-y-2.5">
                        {answers.map((a, i) => (
                          <div key={i} className="grid gap-2 rounded-xl border border-border p-3">
                            <div className="flex items-center justify-between gap-2"><span className="text-[12.5px] font-semibold">{a.question}</span>{a.source === 'user' && <Pill tone="info">You</Pill>}</div>
                            <textarea className="jh-input" rows={2} value={a.answer} onChange={(ev) => setAnswers((list) => list.map((x, j) => (j === i ? { ...x, answer: ev.target.value } : x)))} />
                          </div>
                        ))}
                        {!answers.length && <p className="jh-muted">No answers prepared.</p>}
                      </div>
                      {JSON.stringify(answers) !== JSON.stringify(d.answers) && <div className="mt-3 flex justify-end"><Button size="sm" loading={busy === 'answers'} onClick={() => run('answers', () => jobHunterService.updateApplication(app.applicationUid, { answers }), 'Answers saved')}>Save answers</Button></div>}
                    </div>
                  </>
                )}
                <div className="jh-card jh-card-pad grid gap-3 sm:grid-cols-2">
                  <label className="block"><span className="jh-field-label">Application reference</span><input className="jh-input" placeholder="e.g. candidate ID from the portal" value={externalRef} onChange={(ev) => setExternalRef(ev.target.value)} /></label>
                  <label className="block sm:col-span-2"><span className="jh-field-label">Notes</span><textarea className="jh-input" rows={3} value={notes} onChange={(ev) => setNotes(ev.target.value)} placeholder="Recruiter name, interview dates, salary talk…" /></label>
                  {(notes !== (app.notes ?? '') || externalRef !== (app.externalRef ?? '')) && <div className="flex justify-end sm:col-span-2"><Button size="sm" loading={busy === 'notes'} onClick={() => run('notes', () => jobHunterService.updateApplication(app.applicationUid, { notes, externalRef }), 'Saved')}>Save</Button></div>}
                </div>
              </div>
            )}

            {tab === 'activity' && (
              <div className="jh-card jh-card-pad">
                <div className="jh-timeline">
                  {[...app.history].reverse().map((h, i) => (
                    <div key={i} className="jh-tl-item" style={{ ['--jh-dot' as string]: `var(--color-${STATUS_META[h.status as JhStatus]?.tone === 'neutral' ? 'border-strong' : STATUS_META[h.status as JhStatus]?.tone ?? 'primary'})` }}>
                      <div className="flex flex-wrap items-center gap-2 text-[13px] font-semibold">{STATUS_META[h.status as JhStatus]?.label ?? h.status}<span className="text-[11.5px] font-normal text-text-muted">{fmtDateTime(h.at)} · {h.by === 'agent' ? 'Agent' : h.by === 'whatsapp' ? 'WhatsApp' : 'You'}</span></div>
                      {h.note && <div className="mt-0.5 text-[12.5px] text-text-secondary">{h.note}</div>}
                    </div>
                  ))}
                </div>
                <div className="mt-2 grid gap-2 border-t border-border pt-3 text-[12px] text-text-muted sm:grid-cols-2">
                  <div>Alert sent: {app.notifiedAt ? `${fmtDateTime(app.notifiedAt)} (${app.notifiedVia})` : 'not yet'}</div>
                  <div>Decided: {app.decidedAt ? `${fmtDateTime(app.decidedAt)} via ${app.decisionChannel}` : '—'}</div>
                  {app.appliedAt && <div>Applied: {fmtDate(app.appliedAt)}</div>}
                  {app.feedback && <div>Feedback: {FEEDBACK_LABELS[app.feedback.reason] ?? app.feedback.reason}</div>}
                </div>
              </div>
            )}
          </div>

          <div className="jh-drawer-foot">
            {openStatus && <>
              <Button loading={busy === 'apply'} disabled={app.jobClosed || !!busy} iconLeft={<Send size={14} />} onClick={() => decide('apply')}>Approve &amp; prepare</Button>
              {app.status === 'saved'
                ? <Button variant="secondary" disabled={!!busy} iconLeft={<Undo2 size={14} />} onClick={() => decide('unsave')}>Unsave</Button>
                : <Button variant="secondary" disabled={!!busy} iconLeft={<Bookmark size={14} />} onClick={() => decide('save')}>Save</Button>}
              <div className="relative">
                <Button variant="ghost" disabled={!!busy} iconLeft={<SkipForward size={14} />} onClick={() => setSkipOpen((v) => !v)}>Skip</Button>
                {skipOpen && (
                  <div className="absolute bottom-12 left-0 z-10 w-56 rounded-xl border border-border bg-surface p-1.5 shadow-elevated">
                    <button type="button" className="w-full rounded-lg px-3 py-2 text-left text-[13px] font-semibold hover:bg-surface-alt" onClick={() => decide('skip')}>Just skip</button>
                    {Object.entries(FEEDBACK_LABELS).map(([k, l]) => <button key={k} type="button" className="w-full rounded-lg px-3 py-1.5 text-left text-[12.5px] text-text-secondary hover:bg-surface-alt" onClick={() => decide('skip', k)}>{l}</button>)}
                  </div>
                )}
              </div>
            </>}
            {prepStatus && <>
              {link && <a href={link} target="_blank" rel="noreferrer"><Button iconRight={<ArrowUpRight size={14} />}>Open application</Button></a>}
              <Button variant="secondary" loading={busy === 'applied'} iconLeft={<BookmarkCheck size={14} />} onClick={() => setStatus('applied', 'Marked as applied')}>Mark as applied</Button>
              <Button variant="ghost" loading={busy === 'withdrawn'} onClick={() => setStatus('withdrawn', 'Withdrawn')}>Withdraw</Button>
            </>}
            {app.status === 'applied' && <>
              <Button loading={busy === 'interview'} onClick={() => setStatus('interview', 'Interview logged 🎉')}>Interview</Button>
              <Button variant="secondary" loading={busy === 'offer'} onClick={() => setStatus('offer', 'Offer logged 🏆')}>Offer</Button>
              <Button variant="ghost" loading={busy === 'rejected'} onClick={() => setStatus('rejected', 'Logged as rejected')}>Rejected</Button>
            </>}
            {app.status === 'interview' && <>
              <Button loading={busy === 'offer'} onClick={() => setStatus('offer', 'Offer logged 🏆')}>Offer</Button>
              <Button variant="ghost" loading={busy === 'rejected'} onClick={() => setStatus('rejected', 'Logged as rejected')}>Rejected</Button>
            </>}
            {['skipped', 'expired', 'withdrawn', 'rejected'].includes(app.status) && !app.jobClosed && (
              <Button variant="secondary" loading={busy === 'proposed'} iconLeft={<Undo2 size={14} />} onClick={() => setStatus('proposed', 'Back in matches')}>Move back to matches</Button>
            )}
            <span className={clsx('ml-auto text-[11.5px] text-text-muted')}>Scored {timeAgo(e.at)}</span>
          </div>
        </>
      )}
    </Drawer>
  );
}
